import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { adminAnalyticsService } from '../services/adminAnalyticsService'
import AppHeader from '../components/AppHeader'
import ProfileDropdown from '../components/ProfileDropdown'
import { 
  ChartBarIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  CameraIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  EyeIcon,
  Cog6ToothIcon,
  ShieldCheckIcon,
  BellIcon
} from '@heroicons/react/24/outline'

interface AnalyticsData {
  totalUsers: number
  activeUsers: number
  newUsersToday: number
  totalRevenue: number
  monthlyRevenue: number
  totalShoots: number
  totalInvoices: number
  bugReports: {
    total: number
    open: number
    critical: number
  }
  userGrowth: number
  revenueGrowth: number
}

const AdminDashboardPage = () => {
  const navigate = useNavigate()
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d')

  useEffect(() => {
    loadAnalytics()
  }, [timeRange])

  const loadAnalytics = async () => {
    try {
      setLoading(true)
      const data = await adminAnalyticsService.getOverviewAnalytics(timeRange)
      setAnalytics(data)
    } catch (error) {
      console.error('Error loading analytics:', error)
    } finally {
      setLoading(false)
    }
  }

  const adminModules = [
    {
      title: 'User Management',
      description: 'Manage users, roles, and permissions',
      icon: UserGroupIcon,
      color: 'from-blue-500 to-cyan-500',
      path: '/admin/users',
      stats: analytics?.totalUsers || 0
    },
    {
      title: 'Bug Reports',
      description: 'View and manage user bug reports',
      icon: ExclamationTriangleIcon,
      color: 'from-red-500 to-orange-500',
      path: '/admin/bug-reports',
      stats: analytics?.bugReports.open || 0,
      urgent: (analytics?.bugReports.critical || 0) > 0
    },
    {
      title: 'Analytics & Reports',
      description: 'Detailed analytics and business insights',
      icon: ChartBarIcon,
      color: 'from-purple-500 to-pink-500',
      path: '/admin/analytics',
      stats: `${analytics?.userGrowth || 0}%`
    },

    {
      title: 'Content Management',
      description: 'Manage shoots, clients, and content',
      icon: CameraIcon,
      color: 'from-indigo-500 to-blue-500',
      path: '/admin/content',
      stats: analytics?.totalShoots || 0
    },
    {
      title: 'System Settings',
      description: 'Configure system-wide settings',
      icon: Cog6ToothIcon,
      color: 'from-gray-500 to-slate-500',
      path: '/admin/settings',
      stats: 'Config'
    },
    {
      title: 'Security & Audit',
      description: 'Security logs and audit trails',
      icon: ShieldCheckIcon,
      color: 'from-yellow-500 to-amber-500',
      path: '/admin/security',
      stats: 'Secure'
    },
    {
      title: 'Notifications',
      description: 'System notifications and alerts',
      icon: BellIcon,
      color: 'from-teal-500 to-cyan-500',
      path: '/admin/notifications',
      stats: 'Active'
    }
  ]

  const quickStats = [
    {
      label: 'Total Users',
      value: analytics?.totalUsers || 0,
      change: analytics?.userGrowth || 0,
      icon: UserGroupIcon,
      color: 'text-blue-400'
    },
    {
      label: 'Active Today',
      value: analytics?.activeUsers || 0,
      change: 12,
      icon: EyeIcon,
      color: 'text-green-400'
    },
    {
      label: 'Total Shoots',
      value: analytics?.totalShoots || 0,
      change: 5,
      icon: CameraIcon,
      color: 'text-purple-400'
    },
    {
      label: 'Open Bug Reports',
      value: analytics?.bugReports.open || 0,
      change: -5,
      icon: ExclamationTriangleIcon,
      color: 'text-red-400'
    }
  ]

  const formatChange = (change: number) => {
    const isPositive = change >= 0
    return (
      <div className={`flex items-center space-x-1 ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
        {isPositive ? (
          <ArrowTrendingUpIcon className="w-4 h-4" />
        ) : (
          <ArrowTrendingDownIcon className="w-4 h-4" />
        )}
        <span className="text-sm font-medium">{Math.abs(change)}%</span>
      </div>
    )
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <AppHeader title="Admin Dashboard" subtitle="System overview and management">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2 px-3 py-1 bg-red-500/20 border border-red-500/30 rounded-full">
            <div className="w-2 h-2 bg-red-400 rounded-full animate-pulse"></div>
            <span className="text-red-400 text-sm font-medium">Admin Panel</span>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-gray-400 hover:text-white transition-colors font-medium"
          >
            User Dashboard
          </button>
          <ProfileDropdown />
        </div>
      </AppHeader>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        {/* Time Range Selector */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-between items-center mb-8"
        >
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">System Overview</h1>
            <p className="text-gray-400">Monitor your platform's performance and manage operations</p>
          </div>
          <div className="flex items-center space-x-2">
            {(['7d', '30d', '90d'] as const).map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  timeRange === range
                    ? 'bg-blue-500 text-white'
                    : 'bg-white/10 text-gray-400 hover:text-white hover:bg-white/20'
                }`}
              >
                {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : '90 Days'}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Quick Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          {quickStats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.05 }}
              className="glass-mockup rounded-2xl p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <stat.icon className={`w-8 h-8 ${stat.color}`} />
                {formatChange(stat.change)}
              </div>
              <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
              <div className="text-gray-400 text-sm">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Admin Modules Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <h2 className="text-2xl font-bold text-white mb-6">Admin Modules</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {adminModules.map((module, index) => (
              <motion.button
                key={module.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.05 }}
                onClick={() => navigate(module.path)}
                className="glass-mockup rounded-2xl p-6 text-left hover:scale-105 transition-all group relative"
                whileHover={{ y: -5 }}
              >
                {module.urgent && (
                  <div className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 rounded-full animate-pulse"></div>
                )}
                
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${module.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <module.icon className="w-6 h-6 text-white" />
                </div>
                
                <h3 className="text-white font-semibold mb-2">{module.title}</h3>
                <p className="text-gray-400 text-sm mb-4">{module.description}</p>
                
                <div className="flex items-center justify-between">
                  <span className="text-blue-400 text-sm font-medium">Manage →</span>
                  <span className="text-white font-bold">{module.stats}</span>
                </div>
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Recent Activity & System Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8"
        >
          {/* Recent Activity */}
          <div className="glass-mockup rounded-2xl p-6">
            <h3 className="text-xl font-bold text-white mb-6">Recent Admin Activity</h3>
            <div className="space-y-4">
              {loading ? (
                <div className="text-center py-4">
                  <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                  <p className="text-gray-400 text-sm">Loading activity...</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center space-x-4 p-3 rounded-xl hover:bg-white/5 transition-colors">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <div className="flex-1">
                      <p className="text-white font-medium">New user registration</p>
                      <p className="text-gray-400 text-sm">john.doe@example.com joined</p>
                    </div>
                    <span className="text-gray-500 text-xs">2 min ago</span>
                  </div>
                  
                  <div className="flex items-center space-x-4 p-3 rounded-xl hover:bg-white/5 transition-colors">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    <div className="flex-1">
                      <p className="text-white font-medium">Critical bug report</p>
                      <p className="text-gray-400 text-sm">Payment processing issue</p>
                    </div>
                    <span className="text-gray-500 text-xs">15 min ago</span>
                  </div>
                  
                  <div className="flex items-center space-x-4 p-3 rounded-xl hover:bg-white/5 transition-colors">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <div className="flex-1">
                      <p className="text-white font-medium">System backup completed</p>
                      <p className="text-gray-400 text-sm">Daily backup successful</p>
                    </div>
                    <span className="text-gray-500 text-xs">1 hour ago</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* System Status */}
          <div className="glass-mockup rounded-2xl p-6">
            <h3 className="text-xl font-bold text-white mb-6">System Status</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-xl bg-green-500/10 border border-green-500/30">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-white font-medium">Database</span>
                </div>
                <span className="text-green-400 text-sm">Operational</span>
              </div>
              
              <div className="flex items-center justify-between p-3 rounded-xl bg-green-500/10 border border-green-500/30">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-white font-medium">API Services</span>
                </div>
                <span className="text-green-400 text-sm">Operational</span>
              </div>
              
              <div className="flex items-center justify-between p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/30">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <span className="text-white font-medium">Email Service</span>
                </div>
                <span className="text-yellow-400 text-sm">Degraded</span>
              </div>
              
              <div className="flex items-center justify-between p-3 rounded-xl bg-green-500/10 border border-green-500/30">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-white font-medium">File Storage</span>
                </div>
                <span className="text-green-400 text-sm">Operational</span>
              </div>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  )
}

export default AdminDashboardPage 